/**
 * Graph workflow UI utilities
 * 
 * All graph definitions come from the backend API.
 * This file contains only conversion utilities.
 */

import { getLayoutedElements } from '../utils/layout';

/**
 * Convert backend serialized graph to React Flow format with auto-layout
 * 
 * @param {Object} backendGraph - Serialized graph from backend API
 * @returns {Object} - { nodes, edges, nodeConfigs, stateSchema }
 */
export async function convertBackendGraphToReactFlow(backendGraph) {
  if (!backendGraph || !backendGraph.nodes) {
    return { nodes: [], edges: [], nodeConfigs: {}, stateSchema: null };
  }

  // Convert nodes to React Flow format
  const nodes = backendGraph.nodes.map(nodeDef => {
    const nodeType = nodeDef.type || nodeDef.data?.nodeType || nodeDef.id;
    
    // Check if this is a decision node
    if (nodeType === 'decision') {
      return {
        id: nodeDef.id,
        type: 'decision',
        data: {
          conditional: true
        },
        position: nodeDef.position || { x: 0, y: 0 }
      };
    }
    
    // Regular workflow node - label comes from backend data
    return {
      id: nodeDef.id,
      type: 'workflowNode',
      data: {
        label: nodeDef.data?.label || nodeType,
        nodeType: nodeType,
        status: 'pending',
        configurable: !!backendGraph.nodeConfigs?.[nodeDef.id]?.prompt
      },
      position: nodeDef.position || { x: 0, y: 0 }
    };
  });

  // Convert edges to React Flow format
  const edges = (backendGraph.edges || []).map((edgeDef, index) => {
    const edge = {
      id: edgeDef.id || `e${index + 1}`,
      source: edgeDef.source,
      target: edgeDef.target,
      type: 'smoothstep'
    };
    
    if (edgeDef.label) {
      edge.label = edgeDef.label;
    }
    
    // Preserve source/target handles if they exist in backend data
    if (edgeDef.sourceHandle) {
      edge.sourceHandle = edgeDef.sourceHandle;
    }
    if (edgeDef.targetHandle) {
      edge.targetHandle = edgeDef.targetHandle;
    }
    
    // Handle conditional edges
    if (edgeDef.data?.conditionalCode) {
      const isValidPath = edgeDef.label?.toLowerCase().includes('valid') && 
                          !edgeDef.label?.toLowerCase().includes('invalid');
      edge.style = { stroke: isValidPath ? '#52c41a' : '#ff9800', strokeWidth: 2 };
      edge.animated = true;
      edge.data = {
        conditionalCode: edgeDef.data.conditionalCode,
        isConditional: true
      };
    }
    
    return edge;
  });

  // Assign handles for proper connection points
  assignEdgeHandles(nodes, edges);

  // Apply auto-layout if positions are all at origin
  const needsLayout = nodes.every(n => n.position.x === 0 && n.position.y === 0);
  let layoutedNodes = nodes;
  let layoutedEdges = edges;
  
  if (needsLayout) {
    const result = await getLayoutedElements(nodes, edges);
    layoutedNodes = result.nodes;
    layoutedEdges = result.edges;
  }

  return {
    nodes: layoutedNodes,
    edges: layoutedEdges,
    nodeConfigs: backendGraph.nodeConfigs || {},
    stateSchema: backendGraph.stateSchema || null
  };
}

/**
 * Assign source/target handles for proper edge rendering
 * Only assigns handles if they don't already exist (fallback for legacy data)
 */
function assignEdgeHandles(nodes, edges) {
  const edgeGroups = new Map();
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  
  edges.forEach(edge => {
    if (!edgeGroups.has(edge.source)) {
      edgeGroups.set(edge.source, []);
    }
    edgeGroups.get(edge.source).push(edge);
  });
  
  edgeGroups.forEach((groupEdges, source) => {
    const sourceNode = nodeMap.get(source);
    const isDecision = sourceNode?.type === 'decision';
    
    if (groupEdges.length > 1 && isDecision) {
      groupEdges.forEach((edge, idx) => {
        // Only assign if not already set
        if (!edge.sourceHandle) edge.sourceHandle = idx === 0 ? 'left' : 'right';
        if (!edge.targetHandle) edge.targetHandle = 'top';
      });
    } else {
      groupEdges.forEach(edge => {
        // Only assign if not already set
        if (!edge.sourceHandle) edge.sourceHandle = 'bottom';
        if (!edge.targetHandle) edge.targetHandle = 'top';
      });
    }
  });
}
